/**
 * Session 50 §5d.bis + §5f.bis — PWA team WebSocket client.
 *
 * Subscribes to `/ws/games/{code}/team` and dispatches incoming TeamEvent
 * payloads to per-type listeners. Listeners drive the recipient-side UX
 * from §5f.bis (advance overlay, catch-up toast, finish overlay etc).
 *
 * Auth: the WS handshake carries `playerId` + `token` as query params —
 * browsers can't set custom headers on WS opens, so we match the existing
 * chat/spectator WS patterns.
 *
 * Reconnect: reuses `ReconnectingWebSocket` (exponential backoff, 10 max
 * attempts, 30s cap). Team-sync is best-effort — if the socket flaps
 * the team's state is still reachable via GET /player-state polling in
 * location-view, so a temporary WS outage gracefully degrades, not fails.
 */

import { ReconnectingWebSocket } from './websocket';
import { getState } from '../store';
import { reconcileLocationIndex } from '../store-actions';
import { readQueue, clearQueue, type OfflineQueueEntry } from './offline-queue';
import { syncOfflineProgress, type SyncResponse } from './game-api';

/** Wire-level shape of TeamEvent, mirroring backend Game.kt `TeamEvent`. */
export interface TeamEvent {
  type: 'ADVANCE' | 'CAUGHT_UP' | 'FINISH' | 'PENALTY' | 'GEO_VERIFIED' | string;
  submitter?: string | null;
  newIndex?: number;
  fromIndex?: number;
  toIndex?: number;
  penaltyMs?: number | null;
  /** Session 66 — PENALTY only. Signed minutes delta the organizer applied
   *  (server-side clamp-at-zero might make `penaltyMsDelta` differ from the
   *  organizer's requested delta when `penaltyMs + delta` would go below 0).
   *  Paired with `penaltyMs` (new cumulative total) so the toast shows the
   *  delta explicitly — no client-side cache of previous value needed. */
  penaltyMsDelta?: number | null;
  reason?: string | null;
  finishedAt?: number | null;
  /** FINISH only — server-stamped `Teams.startedAt`. Paired with
   *  `finishedAt` + `penaltyMs` so a non-submitting teammate (or a player
   *  force-finished via `orgFinishTeam`) can compute the same
   *  `totalTimeMs = (finishedAt - startedAt) + penaltyMs` the backend
   *  leaderboard uses — without it `state.totalTimeMs` stayed at 0 and
   *  the results screen rendered «Базовое время 00:00 / Итого 00:00». */
  startedAt?: number | null;
  nextRiddleKey?: string | null;
  nextEncryptedRiddle?: string | null;
  nextEncryptedHint?: string | null;
  nextEncryptedHints?: Array<{
    encryptedText: string;
    delaySec: number;
    orderIndex: number;
    type?: string;
  }>;
  /** Session 50 post-ship fix: server-stamped timestamp (ms epoch) for
   *  when the team entered the location pointed at by `newIndex` /
   *  `toIndex`. Carried on ADVANCE and CAUGHT_UP so subscribers re-anchor
   *  their local `locationStartedAt` when they follow the advance. */
  locationEnteredAt?: number | null;
  /** Session 50 post-ship fix #2 — server's wall clock when event was
   *  serialised. PWA pairs it with `locationEnteredAt` to compute a
   *  clock-skew-corrected local anchor, same formula as join. */
  serverNow?: number;
  /** GEO_VERIFIED only — index of the location whose geo step a teammate
   *  just completed. Subscribers on the same currentLocationIndex flip to
   *  site-code phase; subscribers on a different index ignore the event
   *  (they're either ahead already or catching up via CAUGHT_UP). */
  locationIndex?: number | null;
  /** GEO_VERIFIED only — resolved latitude so the receiver can seed its
   *  local `geoVerified.lat` without a server round-trip. */
  lat?: number | null;
  /** GEO_VERIFIED only — resolved longitude. */
  lon?: number | null;
  /** GEO_VERIFIED only — resolved street address for ADDRESS locations. */
  address?: string | null;
  /** Session 70 (#6) — UUID of the player who caused this event. On
   *  GEO_VERIFIED the submitter's own device already shows a local
   *  success state, so we skip the «Игрок X нашёл место!» toast for
   *  self (would show the submitter their own nickname about their
   *  own action). Null/missing on pre-session-70 servers and on
   *  organizer-triggered events (orgAdvanceTeam / orgFinishTeam). */
  submitterPlayerId?: string | null;
}

export type TeamEventHandler = (event: TeamEvent) => void;

/** Module-scoped singleton — one open WS per PWA session. */
let currentWs: ReconnectingWebSocket | null = null;
const handlers = new Set<TeamEventHandler>();
let onlineListenerInstalled = false;

/** Build the WS URL. Query params carry auth because WS handshakes
 *  can't set custom headers (same constraint as chat + spectator WS). */
function buildWsUrl(gameCode: string, playerId: string, playerToken: string): string {
  const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
  const encodedToken = encodeURIComponent(playerToken);
  const encodedPlayerId = encodeURIComponent(playerId);
  return `${proto}//${location.host}/ws/games/${gameCode}/team?playerId=${encodedPlayerId}&token=${encodedToken}`;
}

/**
 * Open (or replace) the team WS connection. Idempotent — calling twice
 * with the same (gameCode, playerId) closes the previous socket and opens
 * a fresh one; the existing handlers stay registered.
 *
 * Automatically installs the `navigator.onLine: false → true` listener
 * once per page load so offline-queue reconciliation fires on reconnect.
 */
export function connectTeamWs(gameCode: string, playerId: string, playerToken: string): void {
  if (!gameCode || !playerId || !playerToken) return;
  // Spectator / legacy-solo joiners never get a teamId — don't open a
  // socket that the backend would immediately reject with 1008.
  // NOTE: field is `playerRole` on GameState, not `role`. The naked `role`
  // typos silently fell through dev/vitest (esbuild skips full typecheck)
  // but the Docker prod build (`tsc && vite build`) caught it.
  if (getState().playerRole === 'SPECTATOR') return;

  if (currentWs) {
    currentWs.close();
    currentWs = null;
  }
  const ws = new ReconnectingWebSocket(
    buildWsUrl(gameCode, playerId, playerToken),
    (connected) => {
      // On connect/reconnect, drain the offline queue. This is the
      // moment §5e.bis describes — "on reconnect, sync".
      if (connected) {
        void drainOfflineQueue(gameCode, playerId);
      }
    }
  );

  // Wildcard: every incoming TeamEvent is dispatched to all registered
  // handlers, and each handler chooses which types it cares about.
  // Matches the spectator-view pattern.
  ws.on('*', (msg) => {
    const evt = msg as TeamEvent;
    handlers.forEach((h) => {
      try {
        h(evt);
      } catch (err) {
        console.error('team-sync handler error:', err, evt);
      }
    });
  });
  ws.connect();
  currentWs = ws;

  installOnlineListener();
}

/** Close the socket and wipe handlers — call on leave-game / teardown. */
export function disconnectTeamWs(): void {
  if (currentWs) {
    currentWs.close();
    currentWs = null;
  }
  handlers.clear();
}

/**
 * Subscribe to TeamEvents. Returns an unsubscribe function. Views that
 * need to react to events (location-view, chat-view, results-view) each
 * call this and hold onto the returned fn to clean up on unmount.
 */
export function onTeamEvent(handler: TeamEventHandler): () => void {
  handlers.add(handler);
  return () => handlers.delete(handler);
}

/**
 * Drain the localStorage offline queue by POSTing it to `/sync`. Called
 * on team-WS reconnect AND on `navigator.onLine: false → true`. No-op
 * when the queue is empty.
 *
 * On success: clear the queue, update local state from server's authoritative
 * response. On 429 (rate limited): keep the queue — we'll retry on the next
 * online transition. On other failures: keep the queue too — eventual
 * consistency beats dropping the player's work.
 */
export async function drainOfflineQueue(gameCode: string, playerId: string): Promise<SyncResponse | null> {
  const queue = readQueue(gameCode);
  if (queue.length === 0) return null;

  try {
    const resp = await syncOfflineProgress(gameCode, {
      playerId,
      locationsCompleted: queue.map((e: OfflineQueueEntry) => ({
        locationId: e.locationId,
        submittedAnswer: e.submittedAnswer,
        wasSkip: e.wasSkip,
        completedAt: e.completedAt,
      })),
    });

    // Reconcile local state to match the server's authoritative view. If
    // any queued entry was rejected (fake answer, stale position, dead race
    // with a teammate's online submit) the server ignores it — we rely on
    // whatever state it returned.
    if (resp.caughtUp || resp.authoritativeIndex > getState().currentLocationIndex) {
      reconcileLocationIndex(resp.authoritativeIndex);
    }

    clearQueue(gameCode);
    return resp;
  } catch (err) {
    // Rate-limited (429) or server error — keep the queue for a later retry.
    // Caller can observe via the promise rejection, but the user-visible
    // effect is the offline chip staying up. Don't log 429 as an error.
    const msg = err instanceof Error ? err.message : String(err);
    if (!/Слишком часто/.test(msg)) {
      console.warn('drainOfflineQueue failed:', msg);
    }
    return null;
  }
}

/** Install a single `online` listener that flushes the queue when the
 *  browser regains connectivity. Guarded so multiple `connectTeamWs` calls
 *  don't stack listeners. */
function installOnlineListener(): void {
  if (onlineListenerInstalled) return;
  onlineListenerInstalled = true;

  window.addEventListener('online', () => {
    const s = getState();
    if (s.gameCode && s.playerId) {
      void drainOfflineQueue(s.gameCode, s.playerId);
    }
  });

  // Visibility also triggers a drain — user resumes the PWA from background,
  // socket might be stale, queue might have pending entries.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && navigator.onLine) {
      const s = getState();
      if (s.gameCode && s.playerId) {
        void drainOfflineQueue(s.gameCode, s.playerId);
      }
      // /investigate 2026-05-02 — Bug 2: force the team WS out of any
      // stuck-CONNECTING state iOS Safari left it in across the bg
      // window. Pre-fix, a stuck handshake meant ADVANCE/CAUGHT_UP
      // frames silently never arrived after resume — players stuck on
      // a stale riddle / leaderboard with no banner indication of why.
      currentWs?.forceReconnect();
    }
  });
  // /investigate 2026-05-02 — Bug 2: pageshow with persisted=true covers
  // the iOS Safari session-restore + bfcache paths that visibilitychange
  // misses. Same forceReconnect dispatch as the visibility handler. The
  // handlers list is module-scoped, so installOnlineListener's idempotency
  // guard prevents double-registration even across reconnect cycles.
  window.addEventListener('pageshow', (e: PageTransitionEvent) => {
    if (!e.persisted) return;
    const s = getState();
    if (s.gameCode && s.playerId) {
      void drainOfflineQueue(s.gameCode, s.playerId);
    }
    currentWs?.forceReconnect();
  });
}

/** Test helper — resets module-scoped state so each Vitest case starts clean. */
export function __resetTeamSyncForTests(): void {
  if (currentWs) {
    currentWs.close();
    currentWs = null;
  }
  handlers.clear();
  onlineListenerInstalled = false;
}
